// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: Policy & legal settings  (rendering a published legal document)
// `PolicyText` renders one section body written in the documents' light markup as real elements (never as HTML): paragraphs, bullet lists, sub-headings, bold, italic and the allowed links
// (in-app /paths use the router). `PolicyPage` is the full customer page — the same hero, breadcrumbs and reading width as the built-in Terms / Privacy pages — used for a published /terms or /privacy
// and for extra documents at /policy/:slug.
// Neighbours: utils/policyDocs (parser), pages/TermsAndConditions, pages/PrivacyPolicy, pages/PolicyDocument, pages/admin/AdminPolicies (preview).
// ─────────────────────────────────────────────────────────────────────────────
import { Fragment } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import PageTransition from "./PageTransition";
import ScrollReveal from "./ScrollReveal";
import Bubbles from "./Bubbles";
import Breadcrumbs from "./Breadcrumbs";
import useDocumentTitle from "../hooks/useDocumentTitle";
import { longDate, parseBody, parseInline, type PublicPolicyFull, type SectionDoc } from "../utils/policyDocs";

const linkClass = "font-semibold text-teal-600 underline-offset-2 hover:text-teal-700 hover:underline dark:text-teal-300";

function Inlines({ text }: { text: string }) {
  return (
    <>
      {parseInline(text).map((p, i) => {
        if (p.kind === "b") return <strong key={i} className="font-semibold text-ocean-950 dark:text-foam-50">{p.text}</strong>;
        if (p.kind === "i") return <em key={i}>{p.text}</em>;
        if (p.kind === "link") {
          return p.href.startsWith("/")
            ? <Link key={i} to={p.href} className={linkClass}>{p.text}</Link>
            : <a key={i} href={p.href} className={linkClass} {...(p.href.startsWith("https:") ? { target: "_blank", rel: "noopener noreferrer" } : {})}>{p.text}</a>;
        }
        return <Fragment key={i}>{p.text}</Fragment>;
      })}
    </>
  );
}

export function PolicyText({ body }: { body: string }) {
  return (
    <>
      {parseBody(body).map((b, i) => {
        if (b.kind === "h3") return <h3 key={i} className="pt-2 font-display text-base font-bold text-ocean-950 dark:text-foam-50">{b.text}</h3>;
        if (b.kind === "ul") {
          return (
            <ul key={i} className="space-y-2">
              {b.items.map((item, k) => (
                <li key={k} className="flex gap-2">
                  <span aria-hidden="true" className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-teal-400" />
                  <span><Inlines text={item} /></span>
                </li>
              ))}
            </ul>
          );
        }
        return <p key={i}><Inlines text={b.text} /></p>;
      })}
    </>
  );
}

export function PolicySections({ sections }: { sections: SectionDoc[] }) {
  return (
    <>
      {sections.map((s, i) => (
        <div key={s.id} id={s.id} className="scroll-mt-24">
          <ScrollReveal delay={i === 0 ? 0 : 0.02}>
            <h2 className="font-display text-xl font-bold text-ocean-950 dark:text-foam-50">{s.heading}</h2>
            <div className="mt-3 space-y-3 text-sm leading-relaxed text-ocean-800/70 dark:text-foam-100/70"><PolicyText body={s.body} /></div>
          </ScrollReveal>
        </div>
      ))}
    </>
  );
}

export default function PolicyPage({ doc }: { doc: PublicPolicyFull }) {
  useDocumentTitle(doc.title);
  return (
    <PageTransition>
      <section className="relative overflow-hidden bg-gradient-to-b from-ocean-950 to-ocean-900 pt-32 pb-24 text-center text-foam-50 sm:pt-40 sm:pb-28">
        <Bubbles count={10} />
        <div className="relative mx-auto max-w-2xl px-6">
          <motion.span initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="text-xs font-semibold uppercase tracking-widest text-teal-300">Legal</motion.span>
          <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="mt-3 font-display text-4xl font-bold sm:text-5xl">{doc.title}</motion.h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="mt-4 text-foam-100/70">
            Effective Date: {longDate(doc.effectiveDate)} · Version {doc.version} · Last Updated: {longDate(doc.publishedAt)}
          </motion.p>
          {doc.summary && <p className="mt-3 text-sm text-foam-100/70">{doc.summary}</p>}
        </div>
      </section>
      <Breadcrumbs />
      <section className="mx-auto max-w-3xl space-y-14 px-6 py-24"><PolicySections sections={doc.sections} /></section>
    </PageTransition>
  );
}

/** Shown while we find out whether staff have published a version: a legal page never flashes the wrong text. */
export function PolicyLoading() {
  return (
    <PageTransition>
      <div className="flex min-h-[70vh] items-center justify-center bg-foam-50 px-6 pt-32 dark:bg-ocean-950">
        <p role="status" className="text-sm text-ocean-800/80 dark:text-foam-100/75">Loading…</p>
      </div>
    </PageTransition>
  );
}
